import type { FullPageType, PageSectionType } from '@/api/types/PageType';
import type { MenuItem, Organization, Page, FullPage, Section, Cta, RemoteImage, Person, SiteStructure } from '@/types';

import pageStructureQuery from '@/api/query/main-query';
import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import type { MenuItemType, SiteStructureType } from './types/SiteStructureType';

import { GRAPHQL_SERVERS } from './graphqlClient';
import defaultPagesQuery from './query/page-query';
import { DeviceLocationSectionType } from './types/Sections/DeviceLocationSectionType';
import { TeamSectionType } from './types/Sections/TeamSectionType';
import { HeroHomeSectionType } from './types/Sections/HeroHomeSectionType';
import { HeroContactSectionType } from './types/Sections/HeroContactSectionType';
import { HeroSectionType } from './types/Sections/HeroSectionType';
import { CtaType } from './types';
import { TestimonialsSectionType } from './types/Sections/TestimonialsSectionType';
import { CarouselSmallSectionType } from './types/Sections/CarouselSmallSectionType';
import { CarouselAlignSectionType } from './types/Sections/CarouselAlignSectionType';
import { GridSectionType } from './types/Sections/GridSectionType';
import { ListSectionType } from './types/Sections/ListSectionType';
import { SimpleActionSectionType } from './types/Sections/SimpleActionSectionType';
import faqPagesQuery from './query/faq-query';
import { FaqSectionType } from './types/Sections/FaqSectionType';
import { FaqSection } from '@/types/sections/FaqSection';
import productPagesQuery from './query/product-query';
import { GallerySection } from '@/types/sections/GallerySection';
import { GallerySectionType } from './types/Sections/GallerySectionType';
import { HighlightsLightSectionType } from './types/Sections/HighlightsSectionType';
import { HighlightsLightSection } from '@/types/sections/HighlightsSection';
import { DetailsSection } from '@/types/sections/DetailsSection';
import { DetailsSectionType } from './types/Sections/DetailsSectionType';
import { CarouselCardsSectionType } from './types/Sections/CarouselCardsSectionType';
import { GridTextSectionType } from './types/Sections/GridTextSectionType';
import { ChipsSectionType } from './types/Sections/ChipsSectionType';

interface SiteStructureResult {
  data: SiteStructure | undefined;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export function transformSiteStructure(data: SiteStructureType): SiteStructure {
  const pages: Page[] = data.pages.nodes.map(
    (node): Page => ({
      uri: node.uri,
      slug: node.slug,
      template: node.template.templateName,
    })
  );

  const menuItems: MenuItem[] = data.menuItems.nodes
    .filter((node) => !node.parentId)
    .map((node) => ({
      id: node.id,
      label: node.label,
      uri: node.uri,
      childItems: node.childItems?.nodes?.map(
        (category: MenuItemType): MenuItem => ({
          id: category.id,
          label: category.label,
          uri: category.uri,
          childItems: category?.childItems?.nodes?.map(
            (child: MenuItemType): MenuItem => ({
              id: child.id,
              label: child.label,
              uri: child.uri,
              childItems: undefined,
            })
          ),
        })
      ),
    }));

  const organization: Organization = {
    address: data.organization.fields.address,
    email: data.organization.fields.email,
    phone: data.organization.fields.phone,
    name: data.organization.fields.name,
    consent: data.organization.fields.consent,
    logo: data.organization.fields.logo.node
      ? {
          url: data.organization.fields.logo.node.sourceUrl,
          title: data.organization.fields.logo.node.title,
        }
      : undefined,
  };

  return { pages, menuItems, organization };
}

export const useGetWebSiteStructure = (): SiteStructureResult => {
  const { data, loading, error, refetch } = useQuery<SiteStructureType>(pageStructureQuery, {
    context: {
      version: '1.0',
      server: GRAPHQL_SERVERS.WORDPRESS,
    },
  });

  // Post-process the data
  const cleanedData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return transformSiteStructure(data);
  }, [data]);

  return { data: cleanedData, loading, error, refetch };
};

// Full Page Request Related

interface PageResult {
  data?: FullPage;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}


// Function to map CtaType to Cta
function mapCtaTypeToCta(ctaType: CtaType): Cta {
  return {
    icon: ctaType.icon,
    url: ctaType.url,
    page: ctaType.page ? { uri: ctaType.page.node.uri } : undefined,
    file: ctaType.file ? { link: ctaType.file.node.link } : undefined,
    title: ctaType.title,
  };
}


function convertToSection(sectionType: PageSectionType, index: string): Section | undefined {
  switch (sectionType.fieldGroupName) {
    case 'SectionsComponentsDevicelocationsLayout':
      {
        const section = sectionType as DeviceLocationSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
        };
      }
    case 'SectionsComponentsTeamLayout':
      {
        const section = sectionType as TeamSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          small: section.small,
          subtitle: section.subtitle,
          team: section.person?.reduce<Person[]>((acc: Person[], p) => {
              acc.push({
                name: p.name,
                role: p.role,
                email: p.email,
                social: p.social,
                image: p.image?.node
                  ? { title: p.image.node.title, url: p.image.node.sourceUrl }
                  : undefined,
              });
            return acc;
          }, []),
        };
      }
    case 'SectionsComponentsHeroHomeLayout':
      {
        const section = sectionType as HeroHomeSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          cta1: section.cta1 ? mapCtaTypeToCta(section.cta1) : undefined,
          cta2: section.cta2 ? mapCtaTypeToCta(section.cta2) : undefined,
          media:  section.media ? {
            preview: section.media?.preview?.node
                  ? { title: section.media?.preview?.node.title, url: section.media?.preview?.node.sourceUrl }
                  : undefined,
            video: section.media?.video ? {
              link: section.media?.video?.node.link
              } : undefined
          } : undefined,
        };
      }
    case 'SectionsComponentsHeroLayout':
      {
        const section = sectionType as HeroSectionType;
        return {
          uniqueId: index + section.title,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          word: section.word,
          image: section.image?.node
            ? { title: section.image.node.title, url: section.image.node.sourceUrl }
            : undefined,
          cta1: section.cta1 ? mapCtaTypeToCta(section.cta1) : undefined,
          cta2: section.cta2 ? mapCtaTypeToCta(section.cta2) : undefined,
        };
      }
    case 'SectionsComponentsHeroContactLayout':
      {
        const section = sectionType as HeroContactSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          word: section.word,
          image: section.image?.node
            ? { title: section.image.node.title, url: section.image.node.sourceUrl }
            : undefined,
          cta1: section.cta1 ? mapCtaTypeToCta(section.cta1) : undefined,
          cta2: section.cta2 ? mapCtaTypeToCta(section.cta2) : undefined,
          locations: section.locations || [],
        };
      }
    case 'SectionsComponentsTestimonialsLayout':
      {
        const section = sectionType as TestimonialsSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          description: section.description,
          testimonials: section.testimonials
        };
      }
    case 'SectionsComponentsCarouselsmallLayout':
      {
        const section = sectionType as CarouselSmallSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          cards: section.cards?.map((card) => ({
            title: card.title,
            subtitle: card.subtitle,
            image: card.image?.node
              ? { title: card.image.node.title, url: card.image.node.sourceUrl }
              : undefined,
            cta1: card.cta1 ? mapCtaTypeToCta(card.cta1) : undefined,
          })),
        };
      }
    case 'SectionsComponentsCarouselcardsLayout':
      {
        const section = sectionType as CarouselCardsSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          cards: section.cards?.map((card) => ({
            title: card.title,
            subtitle: card.subtitle,
            image: card.image?.node
              ? { title: card.image.node.title, url: card.image.node.sourceUrl }
              : undefined,
            cta1: card.cta1 ? mapCtaTypeToCta(card.cta1) : undefined,
          })),
        };
      }
    case 'SectionsComponentsCarouselalignLayout':
      {
        const section = sectionType as CarouselAlignSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          alloptions: section.alloptions,
          cards: section.cards?.map((card) => ({
            title: card.title,
            subtitle: card.subtitle,
            options: card.options,
            image: card.image?.node
              ? { title: card.image.node.title, url: card.image.node.sourceUrl }
              : undefined,
            cta1: card.cta1 ? mapCtaTypeToCta(card.cta1) : undefined,
          })),
        };
      }
    case 'SectionsComponentsGridLayout':
      {
        const section = sectionType as GridSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          layout: section.layout,
          cta1: section.cta1 ? mapCtaTypeToCta(section.cta1) : undefined,
          cards: section.gridCards?.map((card) => ({
            title: card.title,
            subtitle: card.subtitle,
            image: card.image?.node
              ? { title: card.image.node.title, url: card.image.node.sourceUrl }
              : undefined,
            cta1: card.cta1 ? mapCtaTypeToCta(card.cta1) : undefined,
            properties: card.properties,
          })),
        };
      }
    case 'SectionsComponentsGridtextLayout':
      {
        const section = sectionType as GridTextSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          cards: section.listCards?.map((card) => ({
            title: card.title,
            subtitle: card.subtitle,
            icon: card.icon,
            text: card.text,
            word: card.word,
            image: card.image?.node
              ? { title: card.image.node.title, url: card.image.node.sourceUrl }
              : undefined,
            cta1: card.cta1 ? mapCtaTypeToCta(card.cta1) : undefined,
            highlights: card.highlights,
          })),
        };
      }
      case 'SectionsComponentsChipsLayout':
      {
        const section = sectionType as ChipsSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          alignment: section.alignment,
          chips: section.chips,
        };
      }
    case 'SectionsComponentsListLayout':
      {
        const section = sectionType as ListSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          cards: section.listCards?.map((card) => ({
            title: card.title,
            subtitle: card.subtitle,
            icon: card.icon,
            text: card.text,
            word: card.word,
            image: card.image?.node
              ? { title: card.image.node.title, url: card.image.node.sourceUrl }
              : undefined,
            cta1: card.cta1 ? mapCtaTypeToCta(card.cta1) : undefined,
            highlights: card.highlights,
          })),
        };
      }
      case 'SectionsComponentsSimpleactionLayout':
      {
        const section = sectionType as SimpleActionSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          cta1: section.cta1 ? mapCtaTypeToCta(section.cta1) : undefined,
        };
      }
      case 'SectionsComponentsFaqsLayout':
      {
        const section = sectionType as FaqSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          title: section.title,
          subtitle: section.subtitle,
          categories: section.categories,
        } as FaqSection;
      }
      case 'SectionsComponentsGalleryLayout':
      {
        const section = sectionType as GallerySectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          small: section.small,
          title: section.title,
          summary: section.summary,
          labels: section.labels.map((label) => label.label || ''),
          cta1: section.cta1 ? mapCtaTypeToCta(section.cta1) : undefined,
          images: section.images.map((image) => image.image.node
              ? { title: image.image.node.title, url: image.image.node.sourceUrl }
              : undefined)
        } as GallerySection;
      }
      case 'SectionsComponentsHighlightslightLayout':
      {
        const section = sectionType as HighlightsLightSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          small: section.small,
          title: section.title,
          subtitle: section.subtitle,
          highlights: section.highlights,
        } as HighlightsLightSection;
      }
      case 'SectionsComponentsDetailsLayout':
      {
        const section = sectionType as DetailsSectionType;
        return {
          uniqueId: index,
          fieldGroupName: section.fieldGroupName,
          small: section.small,
          title: section.title,
          subtitle: section.subtitle,
          
          tabs: section.tabs ? section.tabs.map(tab => ({
            title: tab.title,
            type: tab.text ? 'description' : 'files',
            key: `${tab.text ? 'description' : 'files'}-${tab.title}`,
            text: tab.text,
            files: tab.files,
          })) : []
        } as DetailsSection;
      }      
    default:
      return undefined;
  }
}

export function transformFullPage(data: FullPageType): FullPage {
  return {
    uri: data.uri,
    slug: data.slug,
    template: data.template?.templateName,
    title: data.title,
    sections: data.sections?.components?.reduce<Section[]>((acc: Section[], section, index) => {
      const newSection = convertToSection(section, index.toString());
      return newSection ? [...acc, newSection] : acc;
    }, []),
    seo: data.seo
      ? {
          title: data.seo.title,
          metaDesc: data.seo.metaDesc,
          twitterImage: data.seo.twitterImage
            ? {
                url: data.seo.twitterImage.node.sourceUrl,
                title: data.seo.twitterImage.node.title,
              }
            : undefined,
          twitterDescription: data.seo.twitterDescription,
          twitterTitle: data.seo.twitterTitle,
        }
      : undefined,
  };
}

export const useGetDefaultPage = (uri: string): PageResult => {
  const { data, loading, error, refetch } = useQuery(defaultPagesQuery, {
    variables: {
      id: uri,
    },
    context: {
      version: '1.0',
      server: GRAPHQL_SERVERS.WORDPRESS,
    },
  });

  // Post-process the data
  const cleanedData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return transformFullPage(data.page);
  }, [data]);

  return { data: cleanedData, loading, error, refetch };
};

export const useGetFaqPage = (uri: string): PageResult => {
  const { data, loading, error, refetch } = useQuery(faqPagesQuery, {
    variables: {
      id: uri,
    },
    context: {
      version: '1.0',
      server: GRAPHQL_SERVERS.WORDPRESS,
    },
  });

  // Post-process the data
  const cleanedData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return transformFullPage(data.page);
  }, [data]);

  return { data: cleanedData, loading, error, refetch };
};

export const useGetProductPage = (uri: string): PageResult => {
  const { data, loading, error, refetch } = useQuery(productPagesQuery, {
    variables: {
      id: uri,
    },
    context: {
      version: '1.0',
      server: GRAPHQL_SERVERS.WORDPRESS,
    },
  });

  // Post-process the data
  const cleanedData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return transformFullPage(data.page);
  }, [data]);

  return { data: cleanedData, loading, error, refetch };
};
